import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Global listener pool to prevent duplicate subscriptions
 * This significantly reduces memory usage and improves performance
 * when multiple components need the same job results
 */
const listenersPool = new Map();
const subscribersPool = new Map();
const resultsCache = new Map();

/**
 * Hook for efficiently loading job results with shared listeners
 * Automatically pools listeners to prevent memory leaks and duplicate queries
 */
export const useResultsPool = (jobIds = []) => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const subscriberIdRef = useRef(null);

  useEffect(() => {
    if (jobIds.length === 0) {
      setResults({});
      setLoading(false);
      return;
    }

    const subscriberId = `subscriber_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    subscriberIdRef.current = subscriberId;

    // Group job IDs into chunks of 10 (Firestore 'in' query limit)
    const chunks = [];
    for (let i = 0; i < jobIds.length; i += 10) {
      chunks.push(jobIds.slice(i, i + 10));
    }

    const subscriberCallbacks = [];

    chunks.forEach(chunk => {
      const chunkKey = chunk.sort().join(',');
      
      // Check if we already have a listener for this chunk
      if (!listenersPool.has(chunkKey)) {
        console.log(`🔗 Creating new listener pool for chunk: ${chunkKey.substring(0, 50)}...`);
        
        const q = query(
          collection(db, 'analysisResults'),
          where('jobId', 'in', chunk)
        );

        const unsubscribe = onSnapshot(q, 
          (snapshot) => {
            const chunkResults = {};
            
            snapshot.docs.forEach(doc => {
              const data = doc.data();
              const jobId = data.jobId;
              
              if (jobId) {
                const resultData = {
                  promptHash: data.provenance?.prompt_hash || null,
                  modelName: data.provenance?.model_name || null,
                  serviceId: data.serviceId || null,
                  structuredData: data.structuredData || null,
                  summary: data.structuredData?.summary || data.summary || null,
                  targets: data.structuredData?.targets || data.targets || [],
                  lastUpdated: Date.now()
                };
                
                chunkResults[jobId] = resultData;
                resultsCache.set(jobId, resultData);
              }
            });

            console.log(`📊 Updated results for chunk ${chunkKey.substring(0, 30)}...: ${Object.keys(chunkResults).length} jobs`);

            // Notify all subscribers for this chunk
            const subscribers = subscribersPool.get(chunkKey) || new Set();
            subscribers.forEach(callback => {
              try {
                callback(chunkResults);
              } catch (err) {
                console.error('Error in results pool callback:', err);
              }
            });
          },
          (error) => {
            console.error(`Error in results listener for chunk ${chunkKey}:`, error);
            
            // Notify subscribers of error
            const subscribers = subscribersPool.get(chunkKey) || new Set();
            subscribers.forEach(callback => {
              try {
                callback(null, error);
              } catch (err) {
                console.error('Error in results pool error callback:', err);
              }
            });
          }
        );

        listenersPool.set(chunkKey, unsubscribe);
        subscribersPool.set(chunkKey, new Set());
      }

      // Add this component as a subscriber
      const subscribers = subscribersPool.get(chunkKey);
      const callback = (chunkResults, error) => {
        if (error) {
          setError(error.message || 'Failed to load results');
          setLoading(false);
          return;
        }

        if (chunkResults) {
          setResults(prev => ({ ...prev, ...chunkResults }));
          setError(null);
        }
        
        setLoading(false);
      };
      
      subscribers.add(callback);
      subscriberCallbacks.push({ chunkKey, callback });

      // Load cached results immediately if available
      const cachedResults = {};
      let hasCachedData = false;
      
      chunk.forEach(jobId => {
        if (resultsCache.has(jobId)) {
          cachedResults[jobId] = resultsCache.get(jobId);
          hasCachedData = true;
        }
      });

      if (hasCachedData) {
        console.log(`⚡ Using cached results for ${Object.keys(cachedResults).length} jobs`);
        callback(cachedResults);
      }
    });

    // Cleanup function
    return () => {
      subscriberCallbacks.forEach(({ chunkKey, callback }) => {
        const subscribers = subscribersPool.get(chunkKey);
        if (subscribers) {
          subscribers.delete(callback);
          
          // If no more subscribers, cleanup the listener
          if (subscribers.size === 0) {
            console.log(`🗑️ Cleaning up listener pool for chunk: ${chunkKey.substring(0, 50)}...`);
            
            const unsubscribe = listenersPool.get(chunkKey);
            if (unsubscribe) {
              unsubscribe();
              listenersPool.delete(chunkKey);
              subscribersPool.delete(chunkKey);
            }
          }
        }
      });
    };
  }, [jobIds.join(',')]);

  return { results, loading, error };
};

/**
 * Utility function to get pool statistics (for debugging/monitoring)
 */
export const getPoolStats = () => {
  return {
    activeListeners: listenersPool.size,
    totalSubscribers: Array.from(subscribersPool.values()).reduce((sum, set) => sum + set.size, 0),
    cachedResults: resultsCache.size,
    poolKeys: Array.from(listenersPool.keys()).map(key => key.substring(0, 30) + '...'),
    memoryUsage: {
      listeners: listenersPool.size,
      subscribers: subscribersPool.size,
      cache: resultsCache.size
    }
  };
};

/**
 * Utility function to clear the cache (useful for testing or memory management)
 */
export const clearResultsCache = () => {
  console.log('🧹 Clearing results cache...');
  resultsCache.clear();
};

/**
 * Force cleanup of all listeners (useful for app shutdown or testing)
 */
export const forceCleanupPool = () => {
  console.log('🧹 Force cleaning up all result listeners...');
  
  // Unsubscribe all listeners
  listenersPool.forEach(unsubscribe => {
    try {
      unsubscribe();
    } catch (err) {
      console.error('Error during force cleanup:', err);
    }
  });
  
  // Clear all pools
  listenersPool.clear();
  subscribersPool.clear();
  resultsCache.clear();
};
